import React, { forwardRef, useState, useMemo } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatCurrency } from "../UserInput";
import { parse } from "date-fns";
import { useMsal } from "@azure/msal-react";
import { fetchCostSheet } from "../dashboards/dashboardApi";

const DeliveriesTable = forwardRef(({ deliveries, title, color }, ref) => {
    const [viewModal, setViewModal] = useState(false);
    const [selectedDelivery, setSelectedDelivery] = useState(null);
    const { instance, accounts } = useMsal();

    // Group deliveries by date
    const groupedDeliveries = useMemo(() => {
        return deliveries.reduce((acc, item) => {
            const dateKey = new Date(item.deliveryDate).toLocaleDateString();
            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(item);
            return acc;
        }, {});
    }, [deliveries]);

    // Rows only carry delivery fields; the details dialog shows the whole cost sheet
    const handleView = async (delivery) => {
        try {
            setSelectedDelivery(await fetchCostSheet(instance, accounts?.[0] || null, delivery.id));
            setViewModal(true);
        } catch (err) {
            alert("Failed to load cost sheet: " + err.message);
        }
    };

    const handleClose = () => {
        setViewModal(false);
        setSelectedDelivery(null);
    };

    return (
        <>
            <Typography
                variant="h6"
                sx={{ color: color || "black", fontWeight: 700, p: 2 }}
            >
                {title}
            </Typography>

            {Object.keys(groupedDeliveries).length === 0 ? (
                <Typography align="center" sx={{ mt: 2 }}>
                    No deliveries found
                </Typography>
            ) : (
                Object.entries(groupedDeliveries).map(([date, orders]) => (
                    <Accordion key={date} ref={ref} sx={{ my: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {date} ({orders.length} deliveries)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Sales Order No</TableCell>
                                            <TableCell>Client Name</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orders.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{item.salesOrderNo}</TableCell>
                                                <TableCell>{item.clientName}</TableCell>
                                                <TableCell>{item.orderStatus}</TableCell>
                                                <TableCell>
                                                    <Button
                                                        onClick={() => handleView(item)}
                                                        variant="contained"
                                                        size="small"
                                                    >
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                ))
            )}

            {/* Modal for details */}
            <Dialog open={viewModal} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Typography variant="h6">View Cost Sheet: {selectedDelivery?.salesOrderNo}</Typography>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <Dialog open={viewModal} onClose={handleClose} fullWidth maxWidth="lg">
                    <DialogTitle>Delivery Details {selectedDelivery?.salesOrderNo}</DialogTitle>
                    <DialogContent dividers sx={{ overflowY: "auto" }}>
                        {selectedDelivery ? (


                            <Box sx={{ p: 2 }}>
                                <Typography variant="h5" align="center" gutterBottom>
                                    COST SHEET
                                </Typography>

                                {/* Top-level details */}
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Date:</strong>{" "}
                                            {selectedDelivery.date
                                                ? new Date(selectedDelivery.date).toLocaleDateString("en-GB")
                                                : "N/A"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Delivery Date:</strong>{" "}
                                            {selectedDelivery.deliveryDate
                                                ? new Date(selectedDelivery.deliveryDate).toLocaleDateString("en-GB")
                                                : "N/A"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Created By:</strong> {selectedDelivery.createdBy}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Email:</strong> {selectedDelivery.userEmail}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Cost Sheet For:</strong> {selectedDelivery.costsheetfor}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Order Status:</strong> {selectedDelivery.orderStatus}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Client Name:</strong> {selectedDelivery.clientName}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Sales Order No:</strong> {selectedDelivery.salesOrderNo}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Sales SPOC:</strong> {selectedDelivery.salesPOC}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>SCM SPOC:</strong> {selectedDelivery.scmspoc}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Client Payment Terms:</strong>{" "}
                                            {selectedDelivery.clientPaymentTerms}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography>
                                            <strong>Location:</strong> {selectedDelivery.location}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography>
                                            <strong>Approval Status:</strong>{" "}
                                            {selectedDelivery.pending === "true"
                                                ? `Approved By ${selectedDelivery.approvedBy?.join(", ")}`
                                                : selectedDelivery.pending === "false"
                                                    ? `Rejected By ${selectedDelivery.approvedBy?.join(
                                                        ", "
                                                    )} - Note: ${selectedDelivery.remarks}`
                                                    : "Pending"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography>
                                            <strong>Cost Sheet Remarks:</strong>{" "}
                                            {selectedDelivery.costSheetRemarks}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {/* Items Table */}
                                <TableContainer
                                    component={Paper}
                                    elevation={1}
                                    sx={{ mt: 2, border: "1px solid black" }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Details</TableCell>
                                                <TableCell>Qty</TableCell>
                                                <TableCell>GST %</TableCell>
                                                <TableCell>PP</TableCell>
                                                <TableCell>NLC</TableCell>
                                                <TableCell>SP</TableCell>
                                                <TableCell>NSP</TableCell>
                                                <TableCell>Total PP</TableCell>
                                                <TableCell>Total SP</TableCell>
                                                <TableCell>Margin</TableCell>
                                                <TableCell>Margin %</TableCell>
                                                <TableCell>Vendor</TableCell>
                                                <TableCell>Vendor Terms</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedDelivery.items?.map((item, index) => {
                                                // Safe parsing of numbers
                                                const pp = parseFloat(item.pp) || 0;
                                                const nlc = parseFloat(item.nlc) || 0;
                                                const sp = parseFloat(item.sp) || 0;
                                                const nsp = parseFloat(item.nsp) || 0;
                                                const totalPp = parseFloat(item.totalPp) || 0;
                                                const totalSp = parseFloat(item.totalSp) || 0;

                                                const margin = totalSp - totalPp;
                                                const marginPercent = totalPp > 0 ? ((margin / totalPp) * 100).toFixed(1) : 0;

                                                return (
                                                    <TableRow key={index}>
                                                        <TableCell>{item.details}</TableCell>
                                                        <TableCell>{item.qty}</TableCell>
                                                        <TableCell>{item.gst}%</TableCell>
                                                        <TableCell>{formatCurrency(pp)}</TableCell>
                                                        <TableCell>{formatCurrency(nlc)}</TableCell>
                                                        <TableCell>{formatCurrency(sp)}</TableCell>
                                                        <TableCell>{formatCurrency(nsp)}</TableCell>
                                                        <TableCell>{formatCurrency(totalPp)}</TableCell>
                                                        <TableCell>{formatCurrency(totalSp)}</TableCell>
                                                        <TableCell>{formatCurrency(margin)}</TableCell>
                                                        <TableCell>{marginPercent}%</TableCell>
                                                        <TableCell>{item.vendorName}</TableCell>
                                                        <TableCell>{item.vendorPaymentTerms}</TableCell>
                                                    </TableRow>
                                                );
                                            })}

                                            {/* Transportation */}
                                            <TableRow>
                                                <TableCell>Transportation</TableCell>
                                                <TableCell colSpan={6}></TableCell>
                                                <TableCell>
                                                    {/* {formatCurrency(parseFloat(selectedDelivery.items?.[0]?.transportationTotalPp || 0))} */}
                                                    {formatCurrency(parseFloat(selectedDelivery.transportationTotalPp || 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(parseFloat(selectedDelivery.transportationTotalSp || 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const pp = parseFloat(selectedDelivery.transportationTotalPp || 0);
                                                        const sp = parseFloat(selectedDelivery.transportationTotalSp || 0);
                                                        return formatCurrency(sp - pp);
                                                    })()}
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const pp = parseFloat(selectedDelivery.transportationTotalPp || 0);
                                                        const sp = parseFloat(selectedDelivery.transportationTotalSp || 0);
                                                        return pp > 0 ? (((sp - pp) / pp) * 100).toFixed(1) : 0;
                                                    })()}%
                                                </TableCell>
                                                <TableCell colSpan={2}></TableCell>
                                            </TableRow>

                                            {/* Customization */}
                                            <TableRow>
                                                <TableCell>Customization</TableCell>
                                                <TableCell colSpan={6}></TableCell>
                                                <TableCell>
                                                    {formatCurrency(parseFloat(selectedDelivery.customizationTotalPp || 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(parseFloat(selectedDelivery.customizationTotalSp || 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const pp = parseFloat(selectedDelivery.customizationTotalPp || 0);
                                                        const sp = parseFloat(selectedDelivery.customizationTotalSp || 0);
                                                        return formatCurrency(sp - pp);
                                                    })()}
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const pp = parseFloat(selectedDelivery.customizationTotalPp || 0);
                                                        const sp = parseFloat(selectedDelivery.customizationTotalSp || 0);
                                                        return pp > 0 ? (((sp - pp) / pp) * 100).toFixed(1) : 0;
                                                    })()}%
                                                </TableCell>
                                                <TableCell colSpan={2}></TableCell>
                                            </TableRow>

                                            {/* Marketing */}
                                            <TableRow>
                                                <TableCell>Marketing</TableCell>
                                                <TableCell colSpan={6}></TableCell>
                                                <TableCell>
                                                    {formatCurrency(parseFloat(selectedDelivery.marketingTotalPp || 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(parseFloat(selectedDelivery.marketingTotalSp || 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const pp = parseFloat(selectedDelivery.marketingTotalPp || 0);
                                                        const sp = parseFloat(selectedDelivery.marketingTotalSp || 0);
                                                        return formatCurrency(sp - pp);
                                                    })()}
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const pp = parseFloat(selectedDelivery.marketingTotalPp || 0);
                                                        const sp = parseFloat(selectedDelivery.marketingTotalSp || 0);
                                                        return pp > 0 ? (((sp - pp) / pp) * 100).toFixed(1) : 0;
                                                    })()}%
                                                </TableCell>
                                                <TableCell colSpan={2}></TableCell>
                                            </TableRow>


                                            {/* Totals */}
                                            {(() => {
                                                const itemsTotalPP = selectedDelivery.items?.reduce(
                                                    (total, i) => total + (parseFloat(i.totalPp) || 0),
                                                    0
                                                ) || 0;

                                                const itemsTotalSP = selectedDelivery.items?.reduce(
                                                    (total, i) => total + (parseFloat(i.totalSp) || 0),
                                                    0
                                                ) || 0;

                                                const transportationTotalPp = parseFloat(selectedDelivery.transportationTotalPp) || 0;
                                                const transportationTotalSp = parseFloat(selectedDelivery.transportationTotalSp) || 0;
                                                const customizationTotalPp = parseFloat(selectedDelivery.customizationTotalPp) || 0;
                                                const customizationTotalSp = parseFloat(selectedDelivery.customizationTotalSp) || 0;
                                                const marketingTotalPp = parseFloat(selectedDelivery.marketingTotalPp) || 0;
                                                const marketingTotalSp = parseFloat(selectedDelivery.marketingTotalSp) || 0;

                                                const totalPP = itemsTotalPP + transportationTotalPp + customizationTotalPp + marketingTotalPp;
                                                const totalSP = itemsTotalSP + transportationTotalSp + customizationTotalSp + marketingTotalSp;
                                                const totalMargin = totalSP - totalPP;
                                                const totalMarginPercent = totalPP > 0 ? (totalMargin / totalPP) * 100 : 0;

                                                return (
                                                    <TableRow sx={{ "& > *": { fontWeight: "bold" } }}>
                                                        <TableCell>Total</TableCell>
                                                        <TableCell colSpan={6}></TableCell>
                                                        <TableCell>{formatCurrency(totalPP)}</TableCell>
                                                        <TableCell>{formatCurrency(totalSP)}</TableCell>
                                                        <TableCell>{formatCurrency(totalMargin)}</TableCell>
                                                        <TableCell>{totalMarginPercent.toFixed(1)}%</TableCell>
                                                        <TableCell colSpan={2}></TableCell>
                                                    </TableRow>
                                                );
                                            })()}
                                        </TableBody>

                                    </Table>
                                </TableContainer>
                            </Box>
                        ) : (
                            <Typography sx={{ p: 2 }}>No data available.</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Close</Button>
                    </DialogActions>
                </Dialog>

            </Dialog>
        </>
    );
});

export default DeliveriesTable;
